import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Mail, ShieldCheck, Smartphone, UserRound } from 'lucide-react';
import {
  CONTACT_CHANNEL_LABELS,
  MFA_METHOD_LABELS,
  ROLE_LABELS,
  type ContactChallenge,
  type ContactState,
  type ContactStatus,
  type MfaChallengeView,
  type MfaMethod,
  type MfaStatus,
  type RecoveryCodesResponse,
  type SessionUser,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { Button, Card, ErrorNotice, Field, Input, PageHeader, Pill, Spinner } from '../components/ui';

export function ProfilePage() {
  const { session } = useSession();
  const user = session?.user;

  return (
    <>
      <PageHeader title="Your profile" description="Your account details, sign-in and password." />
      {!user ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-5">
          <NameCard user={user} />
          <ContactCard />
          <TwoFactorCard />
          <PasswordCard />
        </div>
      )}
    </>
  );
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// --- Name --------------------------------------------------------------------

function NameCard({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(user.name);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => setName(user.name), [user.name]);

  const save = useMutation({
    mutationFn: () => api.patch('/auth/me', { name: name.trim() }),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['session'] });
    },
    onError: (err) => {
      setSaved(false);
      setError(errorText(err, 'Could not save'));
    },
  });

  const dirty = name.trim() !== user.name;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <UserRound className="size-4 text-muted" />
          Account
          <Pill tone="neutral">{ROLE_LABELS[user.role]}</Pill>
        </span>
      }
    >
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
        className="grid gap-4 p-5 sm:grid-cols-2"
      >
        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
            }}
            required
            autoComplete="name"
          />
        </Field>
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" variant="primary" size="sm" disabled={!dirty} loading={save.isPending}>
            Save
          </Button>
          {saved && !dirty && <span className="text-xs text-success">Saved.</span>}
        </div>
        {error && (
          <div className="sm:col-span-2">
            <ErrorNotice error={new Error(error)} />
          </div>
        )}
      </form>
    </Card>
  );
}

// --- Contact details ---------------------------------------------------------

/**
 * Email and phone, each behind a confirmation code.
 *
 * The screen is deliberately explicit that nothing has moved yet: the heading
 * still shows the old value while a change is pending, and the pending block
 * says what it is waiting for. A person who sees their new address displayed
 * before they have confirmed it will assume the job is done.
 */
function ContactCard() {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['auth', 'contact'],
    queryFn: () => api.get<ContactStatus>('/auth/me/contact'),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['auth', 'contact'] });
    await queryClient.invalidateQueries({ queryKey: ['session'] });
  };

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Mail className="size-4 text-muted" />
          Contact details
        </span>
      }
    >
      {status.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : status.error ? (
        <div className="p-5">
          <ErrorNotice error={status.error} />
        </div>
      ) : status.data ? (
        <div className="divide-y divide-line">
          <ContactRow state={status.data.email} onChanged={refresh} />
          <ContactRow state={status.data.phone} onChanged={refresh} />
        </div>
      ) : null}
    </Card>
  );
}

function ContactRow({ state, onChanged }: { state: ContactState; onChanged: () => Promise<void> }) {
  const channel = state.channel;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(state.value ?? '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => setValue(state.value ?? ''), [state.value]);

  const start = useMutation({
    mutationFn: () =>
      api.post<ContactChallenge>('/auth/me/contact', {
        channel,
        value: value.trim(),
        currentPassword: password,
      }),
    onSuccess: async (challenge) => {
      setError(null);
      setPassword('');
      setOpen(false);
      setNotice(
        `A code was sent to ${challenge.sentTo}.` +
          (challenge.oldValueNotified
            ? ' Your previous one was told a change was requested.'
            : ''),
      );
      await onChanged();
    },
    onError: (err) => setError(errorText(err, 'Could not send a code')),
  });

  const verify = useMutation({
    mutationFn: () => api.post('/auth/me/contact/verify', { channel, code: code.trim() }),
    onSuccess: async () => {
      setError(null);
      setCode('');
      setNotice('Confirmed.');
      await onChanged();
    },
    onError: (err) => setError(errorText(err, 'Could not confirm')),
  });

  const resend = useMutation({
    mutationFn: () => api.post<ContactChallenge>('/auth/me/contact/resend', { channel }),
    onSuccess: async (challenge) => {
      setError(null);
      setNotice(`Another code was sent to ${challenge.sentTo}.`);
      await onChanged();
    },
    onError: (err) => setError(errorText(err, 'Could not send another code')),
  });

  const cancel = useMutation({
    mutationFn: () => api.post('/auth/me/contact/cancel', { channel }),
    onSuccess: async () => {
      setError(null);
      setCode('');
      setNotice(null);
      await onChanged();
    },
    onError: (err) => setError(errorText(err, 'Could not cancel')),
  });

  const Icon = channel === 'email' ? Mail : Smartphone;

  return (
    <div className="space-y-3 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <Icon className="size-4 text-muted" />
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wide text-muted">
            {CONTACT_CHANNEL_LABELS[channel]}
          </div>
          <div className="truncate text-sm text-ink">
            {state.value ?? <span className="text-muted">Not set</span>}
          </div>
        </div>
        {state.value ? (
          state.verifiedAt ? (
            <Pill tone="success">Confirmed</Pill>
          ) : (
            <Pill tone="warn">Not confirmed</Pill>
          )
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setOpen((o) => !o);
            setError(null);
          }}
        >
          {open ? 'Cancel' : state.value && !state.verifiedAt ? 'Confirm or change' : 'Change'}
        </Button>
      </div>

      {/* Why a channel cannot be used at all. Stated once, plainly, rather than
          surfacing as a failed request after somebody has typed a password. */}
      {!state.delivery.available && (
        <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
          {state.delivery.reason}
        </div>
      )}

      {state.pending && (
        <div className="space-y-3 rounded-lg border border-line bg-surface-2 px-3 py-3">
          <div className="text-sm text-ink">
            Waiting for the code sent to <span className="font-medium">{state.pending.newValue}</span>.
            <span className="text-muted">
              {' '}
              Not changed yet — {state.pending.attemptsLeft} attempt
              {state.pending.attemptsLeft === 1 ? '' : 's'} left.
            </span>
          </div>
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              verify.mutate();
            }}
          >
            <div className="w-40">
              <Field label="Code">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  required
                />
              </Field>
            </div>
            <Button type="submit" size="sm" variant="primary" loading={verify.isPending}>
              Confirm
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={state.pending.resendsLeft === 0}
              loading={resend.isPending}
              onClick={() => resend.mutate()}
            >
              Send another
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              loading={cancel.isPending}
              onClick={() => cancel.mutate()}
            >
              Discard
            </Button>
          </form>
        </div>
      )}

      {open && (
        <form
          className="grid gap-3 rounded-lg border border-line bg-surface-2 p-3 sm:grid-cols-2"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            start.mutate();
          }}
        >
          <Field
            label={channel === 'email' ? 'New email address' : 'New phone number'}
            hint={
              channel === 'email'
                ? 'A code goes to this address. Your current one is told a change was asked for.'
                : 'International format, e.g. +254712345678.'
            }
          >
            <Input
              type={channel === 'email' ? 'email' : 'tel'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
              autoComplete={channel === 'email' ? 'email' : 'tel'}
            />
          </Field>
          <Field label="Current password" hint="Confirms it is really you">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </Field>
          <div className="sm:col-span-2">
            <Button
              type="submit"
              size="sm"
              variant="primary"
              loading={start.isPending}
              disabled={!state.delivery.available}
            >
              Send a code
            </Button>
          </div>
        </form>
      )}

      {notice && <div className="text-xs text-success">{notice}</div>}
      {error && <ErrorNotice error={new Error(error)} />}
    </div>
  );
}

// --- Two-factor --------------------------------------------------------------

/**
 * Enrolling, and the recovery codes.
 *
 * The codes are shown in a block that does not go away on its own and says
 * out loud that this is the only time they will be readable — because it is.
 */
function TwoFactorCard() {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['auth', 'mfa'],
    queryFn: () => api.get<MfaStatus>('/auth/mfa'),
  });

  const [method, setMethod] = useState<MfaMethod>('email');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<MfaChallengeView | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['auth', 'mfa'] });
    await queryClient.invalidateQueries({ queryKey: ['session'] });
  };

  const enroll = useMutation({
    mutationFn: () =>
      api.post<MfaChallengeView>('/auth/mfa/enroll', { method, currentPassword: password }),
    onSuccess: (view) => {
      setError(null);
      setPassword('');
      setChallenge(view);
    },
    onError: (err) => setError(errorText(err, 'Could not start')),
  });

  const confirm = useMutation({
    mutationFn: () =>
      api.post<RecoveryCodesResponse>('/auth/mfa/enroll/verify', {
        token: challenge?.token ?? '',
        code: code.trim(),
      }),
    onSuccess: async (result) => {
      setError(null);
      setChallenge(null);
      setCode('');
      setCodes(result.codes);
      await refresh();
    },
    onError: (err) => setError(errorText(err, 'Could not confirm')),
  });

  const disable = useMutation({
    mutationFn: () => api.post('/auth/mfa/disable', { currentPassword: password }),
    onSuccess: async () => {
      setError(null);
      setPassword('');
      setCodes(null);
      await refresh();
    },
    onError: (err) => setError(errorText(err, 'Could not switch it off')),
  });

  const regenerate = useMutation({
    mutationFn: () =>
      api.post<RecoveryCodesResponse>('/auth/mfa/recovery-codes', { currentPassword: password }),
    onSuccess: async (result) => {
      setError(null);
      setPassword('');
      setCodes(result.codes);
      await refresh();
    },
    onError: (err) => setError(errorText(err, 'Could not make new codes')),
  });

  const data = status.data;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <ShieldCheck className="size-4 text-muted" />
          Two-step sign-in
          {data?.enabled && <Pill tone="success">On</Pill>}
        </span>
      }
    >
      <div className="space-y-4 p-5">
        {status.isLoading && (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        )}
        {status.error && <ErrorNotice error={status.error} />}

        {codes && (
          <RecoveryCodeBlock codes={codes} onDismiss={() => setCodes(null)} />
        )}

        {data && !data.enabled && !challenge && (
          <>
            <p className="text-sm text-muted">
              After your password, a code is sent to you and has to be typed back. You can only
              choose a channel this platform can send on and that you have confirmed.
            </p>
            <div className="space-y-2">
              {data.methods.map((offer) => (
                <label
                  key={offer.method}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                    offer.eligible ? 'border-line' : 'border-line/60 opacity-70'
                  }`}
                >
                  <input
                    type="radio"
                    name="mfa-method"
                    className="mt-1"
                    checked={method === offer.method}
                    disabled={!offer.eligible}
                    onChange={() => setMethod(offer.method)}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm text-ink">
                      {MFA_METHOD_LABELS[offer.method]}
                      {offer.destination && (
                        <span className="text-muted"> — {offer.destination}</span>
                      )}
                    </span>
                    {offer.blockedReason && (
                      <span className="block text-xs text-warn">{offer.blockedReason}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                enroll.mutate();
              }}
            >
              <div className="w-56">
                <Field label="Current password">
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </Field>
              </div>
              <Button
                type="submit"
                size="sm"
                variant="primary"
                loading={enroll.isPending}
                disabled={!data.methods.some((m) => m.method === method && m.eligible)}
              >
                Switch on
              </Button>
            </form>
          </>
        )}

        {challenge && (
          <form
            className="space-y-3 rounded-lg border border-line bg-surface-2 p-3"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              confirm.mutate();
            }}
          >
            <p className="text-sm text-ink">
              A code was sent to <span className="font-medium">{challenge.sentTo}</span>. Type it
              back to finish switching this on.
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-40">
                <Field label="Code">
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    required
                  />
                </Field>
              </div>
              <Button type="submit" size="sm" variant="primary" loading={confirm.isPending}>
                Finish
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setChallenge(null)}>
                Cancel
              </Button>
            </div>
            <p className="text-xs text-muted">
              Your other signed-in devices will be signed out, and you will be given ten recovery
              codes.
            </p>
          </form>
        )}

        {data?.enabled && (
          <div className="space-y-3">
            <p className="text-sm text-ink">
              On, over{' '}
              <span className="font-medium">
                {MFA_METHOD_LABELS[data.method ?? 'email']}
              </span>
              . <span className="text-muted">
                {data.recoveryCodesLeft} recovery code
                {data.recoveryCodesLeft === 1 ? '' : 's'} left.
              </span>
            </p>
            {data.recoveryCodesLeft === 0 && (
              <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
                No recovery codes left. If this channel stops working you will need an
                administrator to switch two-step sign-in off for you. Make a new set now.
              </div>
            )}
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-56">
                <Field label="Current password">
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </Field>
              </div>
              <Button
                size="sm"
                variant="ghost"
                loading={regenerate.isPending}
                disabled={password.length === 0}
                onClick={() => regenerate.mutate()}
              >
                New recovery codes
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={disable.isPending}
                disabled={password.length === 0}
                onClick={() => disable.mutate()}
              >
                Switch off
              </Button>
            </div>
          </div>
        )}

        {error && <ErrorNotice error={new Error(error)} />}
      </div>
    </Card>
  );
}

function RecoveryCodeBlock({ codes, onDismiss }: { codes: string[]; onDismiss: () => void }) {
  return (
    <div className="space-y-3 rounded-lg border border-warn/40 bg-warn-soft p-4">
      <div className="text-sm font-medium text-ink">
        Save these now — this is the only time they are shown.
      </div>
      <p className="text-xs text-muted">
        Each works once, in place of the code we send you. They are what gets you back in if your
        email or phone stops working. They are stored hashed, so nobody, including this platform,
        can read them back.
      </p>
      <ul className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-sm text-ink sm:grid-cols-3">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <Button size="sm" variant="ghost" onClick={onDismiss}>
        I have saved them
      </Button>
    </div>
  );
}

// --- Password ----------------------------------------------------------------

function PasswordCard() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: () =>
      api.post('/auth/password', { currentPassword: form.current, newPassword: form.next }),
    onSuccess: async () => {
      setError(null);
      setDone(true);
      setForm({ current: '', next: '', confirm: '' });
      // Other sessions were signed out; refresh ours to be safe.
      await queryClient.invalidateQueries({ queryKey: ['session'] });
    },
    onError: (err) => {
      setDone(false);
      setError(errorText(err, 'Could not change password'));
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (form.next.length < 10) {
      setError('New password must be at least 10 characters.');
      return;
    }
    if (form.next !== form.confirm) {
      setError('The new passwords do not match.');
      return;
    }
    if (form.next === form.current) {
      setError('New password must differ from the current one.');
      return;
    }
    change.mutate();
  }

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="size-4 text-muted" />
          Password
        </span>
      }
    >
      <form onSubmit={onSubmit} className="grid gap-4 p-5 sm:grid-cols-2">
        <div className="sm:col-span-2 sm:max-w-sm">
          <Field label="Current password">
            <Input
              type="password"
              value={form.current}
              onChange={(e) => {
                setForm({ ...form, current: e.target.value });
                setDone(false);
              }}
              required
              autoComplete="current-password"
            />
          </Field>
        </div>
        <Field label="New password" hint="At least 10 characters">
          <Input
            type="password"
            value={form.next}
            onChange={(e) => {
              setForm({ ...form, next: e.target.value });
              setDone(false);
            }}
            required
            autoComplete="new-password"
          />
        </Field>
        <Field label="Confirm new password">
          <Input
            type="password"
            value={form.confirm}
            onChange={(e) => {
              setForm({ ...form, confirm: e.target.value });
              setDone(false);
            }}
            required
            autoComplete="new-password"
          />
        </Field>
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" variant="primary" size="sm" loading={change.isPending}>
            Change password
          </Button>
          {done && <span className="text-xs text-success">Password changed — other sessions signed out.</span>}
        </div>
        {error && (
          <div className="sm:col-span-2">
            <ErrorNotice error={new Error(error)} />
          </div>
        )}
      </form>
    </Card>
  );
}
